import { gql } from '@apollo/client';

// v1 query
export const GET_LEDGER_ACCOUNTS = gql`
  query getLedgerAccounts($clientId: ID!) {
    ledgerAccounts(clientId: $clientId) {
      accountType
      accountSettings {
        collectionMethod
        optimizationStatus
        volume
        dateActive
        dateInactive
      }
      active
      autoPostEnabled
      category {
        id
      }
      currencyId
      details
      financialInstitutionProduct {
        id
        financialInstitution {
          id
          name
        }
      }
      id
      isTemporary
      name
      order
      role
      thirdPartyLoginAvailable
      statementDayOfMonth
      isAutoGenerated
      accountLastFourDigits
    }
  }
`;

// v2 query
export const GET_LEDGER_ACCOUNTS_BY_CLIENT = gql`
  query getLedgerAccountsByClient($clientId: ID!) {
    ledgerAccountsByClient(clientId: $clientId) {
      accountType
      accountSettings {
        collectionMethod
        optimizationStatus
        volume
        dateActive
        dateInactive
      }
      active
      autoPostEnabled
      category {
        id
      }
      currencyId
      details
      displayGroup {
        id
        name
        parent {
          id
          name
        }
      }
      financialInstitutionProduct {
        id
        financialInstitution {
          id
          name
        }
      }
      id
      isTemporary
      name
      sortOrder
      role
      thirdPartyLoginAvailable
      statementDayOfMonth
      isAutoGenerated
      accountLastFourDigits
    }
  }
`;

// v1 query
export const GET_LEDGER_ACCOUNT = gql`
  query getLedgerAccount($id: ID!) {
    ledgerAccount(id: $id) {
      accountType
      accountSettings {
        collectionMethod
        optimizationStatus
        volume
        dateActive
        dateInactive
      }
      active
      autoPostEnabled
      category {
        id
      }
      currencyId
      details
      financialInstitutionProduct {
        id
        financialInstitution {
          id
          name
        }
      }
      id
      isTemporary
      name
      order
      role
      thirdPartyLoginAvailable
      statementDayOfMonth
      isAutoGenerated
      accountLastFourDigits
    }
  }
`;

// v2 query
export const GET_LEDGER_ACCOUNT_V2 = gql`
  query getLedgerAccount($id: ID!) {
    ledgerAccount(id: $id) {
      accountType
      accountSettings {
        collectionMethod
        optimizationStatus
        volume
        dateActive
        dateInactive
      }
      active
      autoPostEnabled
      category {
        id
      }
      currencyId
      details
      displayGroup {
        id
        name
        parent {
          id
          name
        }
      }
      financialInstitutionProduct {
        id
        financialInstitution {
          id
          name
        }
      }
      id
      isTemporary
      name
      sortOrder
      role
      thirdPartyLoginAvailable
      statementDayOfMonth
      isAutoGenerated
      accountLastFourDigits
    }
  }
`;

const V2_LEDGER_ACCOUNT = gql`
  fragment V2LedgerAccount on LedgerAccount {
    accountType
    accountSettings {
      collectionMethod
      optimizationStatus
      volume
      dateActive
      dateInactive
    }
    active
    autoPostEnabled
    category {
      id
    }
    currencyId
    details
    displayGroup {
      id
      name
      parent {
        id
        name
      }
    }
    financialInstitutionProduct {
      id
      financialInstitution {
        id
        name
      }
    }
    id
    isTemporary
    name
    sortOrder
    role
    thirdPartyLoginAvailable
    statementDayOfMonth
    isAutoGenerated
    accountLastFourDigits
    targetLedgerMappings {
      targetLedgerAccount {
        id
        name
      }
      entryType
    }
  }
`;

export const CREATE_LEDGER_ACCOUNT = gql`
  ${V2_LEDGER_ACCOUNT}
  mutation createLedger($clientId: ID!, $ledgerAccount: CreateLedgerAccountInput!) {
    createLedgerAccount(clientId: $clientId, input: $ledgerAccount) {
      ... on LedgerAccount {
        ...V2LedgerAccount
      }
      ... on InvalidInputError {
        message
        code
      }
    }
  }
`;

export const UPDATE_LEDGER_ACCOUNT = gql`
  ${V2_LEDGER_ACCOUNT}
  mutation updateLedger($ledgerAccountInput: UpdateLedgerAccountInput!) {
    updateLedgerAccount(input: $ledgerAccountInput) {
      ... on LedgerAccount {
        ...V2LedgerAccount
      }
      ... on InvalidInputError {
        message
        code
      }
    }
  }
`;

export const GET_TARGET_LEDGER_MAPPINGS = gql`
  query getTargetLedgerMappingsForLedger($ledgerAccountId: ID!) {
    ledgerAccount(id: $ledgerAccountId) {
      id
      targetLedgerMappings {
        targetLedgerAccount {
          id
          name
        }
        entryType
      }
    }
  }
`;

export const DELETE_LEDGER_ACCOUNT = gql`
  mutation deleteLedgeAccount($ledgerAccountId: ID!) {
    deleteLedgeAccount(ledgerAccountId: $ledgerAccountId)
      @rest(
        type: "DeleteLedgerAccount"
        endpoint: "mainapp"
        method: "DELETE"
        path: "ledgeraccount.json/:ledgerAccountId"
      ) {
      NoResponse
    }
  }
`;

export const GET_ADDONS = gql`
  query getAddons($clientId: ID!) {
    addons(clientId: $clientId)
      @rest(type: "Addons", endpoint: "mainapp", path: "addon.json/client/:clientId") {
      closeDayOfMonth
      accountType
      collectionMethod
      dateActive
      dateInactive
      details
      id
      name
      optimizationStatus
      volume
      thirdPartyLoginAvailable
      squareMerchantId
      stripeId
      order
    }
  }
`;

export const GET_ADDON = gql`
  query getAddon($addonId: ID!) {
    addon(addonId: $addonId)
      @rest(type: "Addon", endpoint: "mainapp", path: "addon.json/:addonId") {
      closeDayOfMonth
      accountType
      collectionMethod
      dateActive
      dateInactive
      details
      id
      name
      optimizationStatus
      volume
      thirdPartyLoginAvailable
      squareMerchantId
      stripeId
      order
    }
  }
`;

export const CREATE_ADDON = gql`
  mutation createAddon($addon: [Object!]!) {
    addon(input: $addon)
      @rest(type: "Addon", endpoint: "mainapp", method: "POST", path: "addon.json") {
      closeDayOfMonth
      accountType
      collectionMethod
      dateActive
      dateInactive
      details
      id
      name
      optimizationStatus
      volume
      thirdPartyLoginAvailable
      squareMerchantId
      stripeId
      order
    }
  }
`;

export const UPDATE_ADDON = gql`
  mutation updateAddon($addon: [Object!]!, $addonId: String!) {
    addon(input: $addon, addonId: $addonId)
      @rest(type: "Addon", endpoint: "mainapp", method: "PUT", path: "addon.json/:addonId") {
      closeDayOfMonth
      accountType
      collectionMethod
      dateActive
      dateInactive
      details
      id
      name
      optimizationStatus
      volume
      thirdPartyLoginAvailable
      squareMerchantId
      stripeId
      order
    }
  }
`;

export const DELETE_ADDON = gql`
  mutation deleteAddon($addonId: ID!) {
    deleteAddon(addonId: $addonId)
      @rest(
        type: "DeleteAddon"
        endpoint: "mainapp"
        method: "DELETE"
        path: "addon.json/:addonId"
      ) {
      NoResponse
    }
  }
`;

export const GET_LEDGER_CATEGORY_ORDER = gql`
  query getLedgerCategoryOrder($clientId: ID!) {
    ledgerCategoryOrders(clientId: $clientId)
      @rest(
        type: "LedgerCategoryOrder"
        endpoint: "mainapp"
        path: "ledgeraccountcategory.json/order/client/:clientId"
      ) {
      id
      categoryId
      type
    }
  }
`;

export const SORT_LEDGER_CATEGORY_ALPHABETICAL = gql`
  mutation sortAlphabetical($clientId: ID!, $categoryId: String) {
    sortAlphabetical(clientId: $clientId, categoryId: $categoryId, input: {}, input: {})
      @rest(
        type: "LedgerCategoryOrder"
        endpoint: "mainapp"
        path: "ledgeraccountcategory.json/order/client/:clientId/category/:categoryId/order_by_alpha"
        method: "PUT"
      ) {
      NoResponse
    }
  }
`;

export const SORT_LEDGER_CATEGORY_BY_ORDER_NUMBER = gql`
  mutation sortByOrderNumber($clientId: ID!, $categoryId: String) {
    sortByOrderNumber(clientId: $clientId, categoryId: $categoryId, input: {})
      @rest(
        type: "LedgerCategoryOrder"
        endpoint: "mainapp"
        path: "ledgeraccountcategory.json/order/client/:clientId/category/:categoryId/order_manual"
        method: "PUT"
      ) {
      NoResponse
    }
  }
`;

export const GET_CLIENT_CURRENCY = gql`
  query getClientCurrency($clientId: ID!) {
    client(id: $clientId) {
      id
      currencyId
    }
  }
`;

export const GET_CURRENCIES = gql`
  query getCurrencies {
    currencies {
      id
      name
      code
    }
  }
`;

export const GET_COUNTRIES = gql`
  query getCountries {
    countries {
      id
      name
      code
      currency {
        id
        code
        name
      }
      regions {
        countryCode
        code
        name
        taxPercent
      }
    }
  }
`;

export const GET_FDS_CONNECTIONS = gql`
  query getFdsAccounts($clientId: ID!) {
    client(id: $clientId) {
      id
      financialConnections {
        id
        financialInstitutionName
        source
        sourceId
        accounts {
          id
          name
          number
          ledger {
            id
          }
        }
      }
    }
  }
`;

export const GET_FINANCIAL_INSTITUTIONS = gql`
  query getFinancialInstitutions {
    financialInstitutions {
      id
      name
      isActive
    }
  }
`;

export const GET_FINANCIAL_INSTITUTION = gql`
  query getFinancialInstitution($id: ID!) {
    financialInstitution(id: $id) {
      id
      name
      isActive
      products {
        id
        financialInstitutionProductType {
          id
          name
          ledgerCategory {
            id
          }
        }
      }
    }
  }
`;

export const CREATE_FINANCIAL_INSTITUTION = gql`
  mutation createFinancialInstitution(
    $institution: FinancialInstitutionRequest!
    $populateDefaults: Boolean!
    $clientId: ID!
  ) {
    createFinancialInstitution(
      institution: $institution
      populateDefaults: $populateDefaults
      clientId: $clientId
    ) {
      id
      name
      isActive
    }
  }
`;

export const MAP_CONNECTION_TO_ACCOUNT = gql`
  mutation mapConnectionToLedger($clientId: ID!, $connection: ConnectionInput!, $accountId: ID!) {
    mapConnectionToAccount(clientId: $clientId, connection: $connection, accountId: $accountId) {
      id
    }
  }
`;

export const UNMAP_FDS_ACCOUNT_LEDGER = gql`
  mutation unmapFDSAccountLedgerMapping($accountId: ID!, $ledgerId: ID, $dryRun: Boolean!) {
    updateAccountLedgerMapping(accountId: $accountId, ledgerId: $ledgerId, dryRun: $dryRun) {
      remappedAccounts {
        accountId
        oldLedgerId
        newLedgerId
      }
    }
  }
`;

export const REORDER_LEDGER_ACCOUNT = gql`
  mutation reorderLedgerAccount($ledgerAccountId: ID!, $reorderId: String!, $isLast: Boolean!) {
    reorderLedgerAccount(
      ledgerAccountId: $ledgerAccountId
      reorderId: $reorderId
      isLast: $isLast
      input: {}
    )
      @rest(
        type: "ReorderLedger"
        endpoint: "mainapp"
        path: "ledgeraccount.json/{args.ledgerAccountId}/reorder_to/{args.reorderId}/{args.isLast}"
        method: "PUT"
      ) {
      NoResponse
    }
  }
`;

export const REORDER_ADDON = gql`
  mutation reorderAddon($addonId: ID!, $reorderId: String!, $isLast: Boolean!) {
    reorderAddon(addonId: $addonId, reorderId: $reorderId, isLast: $isLast, input: {})
      @rest(
        type: "ReorderAddon"
        endpoint: "mainapp"
        path: "addon.json/{args.addonId}/reorder_to/{args.reorderId}/{args.isLast}"
        method: "PUT"
      ) {
      NoResponse
    }
  }
`;

export const GET_LEDGER_ACCOUNT_DISPLAY_GROUPS = gql`
  query getLedgerAccountDisplayGroups {
    ledgerAccountDisplayGroups {
      id
      name
      parent {
        id
        name
      }
    }
  }
`;
